import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send, Briefcase, Wine, Facebook, Instagram } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "../hooks/use-toast";
import { API } from "../lib/api";

const InfoItem = ({ icon: Icon, label, value, href, testid }) => (
  <div className="flex items-start gap-4 group" data-testid={testid}>
    <div className="w-10 h-10 rounded-full bg-[#C1624D]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C1624D]/20 transition-colors duration-300">
      <Icon size={18} className="text-[#C1624D]" />
    </div>
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-[#8C847F] mb-1" style={{ fontFamily: "'Lato', sans-serif" }}>
        {label}
      </p>
      {href ? (
        <a
          href={href}
          className="text-base text-[#2C2420] hover:text-[#C1624D] transition-colors duration-300"
          style={{ fontFamily: "'Lato', sans-serif" }}
        >
          {value}
        </a>
      ) : (
        <p className="text-base text-[#2C2420]" style={{ fontFamily: "'Lato', sans-serif" }}>
          {value}
        </p>
      )}
    </div>
  </div>
);

const RecruitmentBlock = ({ isInView }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", position: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const openPositions = [
    {
      icon: Wine,
      title: t("findus.position_serveur_title"),
      type: t("findus.position_serveur_type"),
      description: t("findus.position_serveur_desc"),
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/recruitment`, form);
      setSent(true);
      setForm({ name: "", email: "", position: "", message: "" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("toast.error_title"),
        description: t("toast.error_recruitment"),
      });
    }
    setSending(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: 0.3 }}
      className="mt-20 pt-20 border-t border-[#D8D0C5]"
      data-testid="recruitment-section"
    >
      {/* Header */}
      <div className="text-center mb-14">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6F7D4E]/10 mb-4">
          <Briefcase size={16} className="text-[#6F7D4E]" />
          <p
            className="text-sm text-[#6F7D4E]"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 400 }}
          >
            {t("findus.recruitment_label")}
          </p>
        </div>
        <h3
          className="text-3xl md:text-4xl font-light tracking-tight text-[#2C2420] mb-3"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
          data-testid="recruitment-heading"
        >
          {t("findus.recruitment_heading")}
        </h3>
        <p
          className="text-base text-[#8C847F] max-w-xl mx-auto"
          style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
        >
          {t("findus.recruitment_subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
        {/* Left: Open positions */}
        <div>
          <p
            className="text-xs uppercase tracking-[0.2em] text-[#8C847F] mb-6"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            {t("findus.open_positions")}
          </p>
          <div className="space-y-4 mb-8">
            {openPositions.map((pos, i) => {
              const Icon = pos.icon;
              return (
                <div
                  key={pos.title}
                  className="flex items-start gap-4 p-5 bg-white/80 border border-[#D8D0C5] rounded-sm
                             hover:shadow-sm transition-shadow duration-300 group"
                  data-testid={`position-${i}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#6F7D4E]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#6F7D4E]/20 transition-colors duration-300">
                    <Icon size={18} className="text-[#6F7D4E]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h4
                        className="text-lg font-normal text-[#2C2420]"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                      >
                        {pos.title}
                      </h4>
                      <span
                        className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full bg-[#C1624D]/10 text-[#C1624D]"
                        style={{ fontFamily: "'Lato', sans-serif" }}
                      >
                        {pos.type}
                      </span>
                    </div>
                    <p
                      className="text-sm text-[#8C847F]"
                      style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                    >
                      {pos.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="p-4 bg-[#FDFBF7] border border-dashed border-[#D8D0C5] rounded-sm">
            <p
              className="text-sm text-[#8C847F] italic text-center"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "0.95rem" }}
            >
              {t("findus.spontaneous")}
            </p>
          </div>
        </div>

        {/* Right: Application form */}
        <div className="bg-white/80 backdrop-blur-sm p-8 md:p-10 border border-[#D8D0C5] rounded-sm shadow-sm">
          <h4
            className="text-2xl font-light text-[#2C2420] mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {t("findus.form_apply")}
          </h4>
          <p
            className="text-sm text-[#8C847F] mb-8"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
          >
            {t("findus.form_position_motivation")}
          </p>

          {sent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-12 text-center"
              data-testid="recruitment-success"
            >
              <div className="w-16 h-16 rounded-full bg-[#6F7D4E]/10 flex items-center justify-center mx-auto mb-4">
                <Briefcase size={24} className="text-[#6F7D4E]" />
              </div>
              <p className="text-xl text-[#2C2420] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {t("findus.form_apply_success")}
              </p>
              <p className="text-sm text-[#8C847F]" style={{ fontFamily: "'Lato', sans-serif" }}>
                {t("findus.form_apply_success_desc")}
              </p>
              <button
                onClick={() => setSent(false)}
                className="mt-6 text-sm text-[#C1624D] underline underline-offset-4 hover:opacity-70 transition-opacity"
                data-testid="recruitment-another-btn"
              >
                {t("findus.apply_another")}
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="recruitment-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#8C847F] mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
                    {t("findus.form_full_name")}
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#D8D0C5] rounded-sm text-[#2C2420]
                               focus:outline-none focus:border-[#6F7D4E] transition-colors duration-300"
                    style={{ fontFamily: "'Lato', sans-serif" }}
                    placeholder={t("findus.form_name_placeholder")}
                    data-testid="recruitment-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#8C847F] mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
                    {t("findus.form_email")}
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#D8D0C5] rounded-sm text-[#2C2420]
                               focus:outline-none focus:border-[#6F7D4E] transition-colors duration-300"
                    style={{ fontFamily: "'Lato', sans-serif" }}
                    placeholder={t("findus.form_email_placeholder")}
                    data-testid="recruitment-email-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#8C847F] mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
                  {t("findus.position_select_label")}
                </label>
                <select
                  value={form.position}
                  onChange={(e) => setForm({ ...form, position: e.target.value })}
                  className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#D8D0C5] rounded-sm text-[#2C2420]
                             focus:outline-none focus:border-[#6F7D4E] transition-colors duration-300 appearance-none"
                  style={{ fontFamily: "'Lato', sans-serif" }}
                  data-testid="recruitment-position-select"
                >
                  <option value="">{t("findus.position_select_placeholder")}</option>
                  <option value="serveur">{t("findus.position_serveur")}</option>
                  <option value="spontanee">{t("findus.position_spontaneous")}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#8C847F] mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
                  {t("findus.form_position_motivation")}
                </label>
                <textarea
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#D8D0C5] rounded-sm text-[#2C2420]
                             focus:outline-none focus:border-[#6F7D4E] transition-colors duration-300 resize-none"
                  style={{ fontFamily: "'Lato', sans-serif" }}
                  placeholder={t("findus.form_position_placeholder")}
                  data-testid="recruitment-message-input"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full py-3 rounded-full bg-[#6F7D4E] text-white font-serif italic text-lg
                           hover:bg-[#5A6840] active:scale-[0.98] transition-all duration-300 shadow-md hover:shadow-lg h-auto"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                data-testid="recruitment-submit-btn"
              >
                {sending ? t("findus.form_sending") : t("findus.form_apply")}
              </Button>
            </form>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const FindUsSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("toast.error_title"),
        description: t("toast.error_contact"),
      });
    }
    setSending(false);
  };

  return (
    <section
      id="nous-trouver"
      data-testid="findus-section"
      className="py-24 md:py-36 px-6 md:px-12 bg-[#F5E6D3]/40"
    >
      <div className="max-w-7xl mx-auto" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p
            className="text-sm uppercase tracking-[0.3em] text-[#C1624D] mb-4"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            {t("findus.label")}
          </p>
          <h2
            className="text-4xl md:text-5xl font-light tracking-tight text-[#2C2420]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="findus-heading"
          >
            {t("findus.heading")}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left: Map + Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            {/* Map */}
            <div
              className="map-container rounded-sm overflow-hidden mb-10 shadow-md"
              data-testid="google-map"
            >
              <iframe
                title="L'Ancienne Poste - Entrechaux"
                src="https://www.google.com/maps?q=44.2176708,5.1384848(L%27Ancienne+Poste)&z=18&hl=fr&output=embed"
                width="100%"
                height="320"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <InfoItem
                icon={MapPin}
                label={t("findus.address")}
                value="243 Avenue des Princes d'Orange, 84340 Entrechaux"
                href="https://maps.google.com/?q=44.2176708,5.1384848"
                testid="info-address"
              />
              <InfoItem
                icon={Phone}
                label={t("findus.phone")}
                value="04 90 46 09 84"
                href="tel:+33490460984"
                testid="info-phone"
              />
              <InfoItem
                icon={Phone}
                label={t("findus.phone_mobile")}
                value="06 34 13 40 35"
                href="tel:+33634134035"
                testid="info-phone-mobile"
              />
              <InfoItem
                icon={Mail}
                label={t("findus.email")}
                value="contact@ancienneposteentrechaux.com"
                href="mailto:contact@ancienneposteentrechaux.com"
                testid="info-email"
              />
              <InfoItem
                icon={Clock}
                label={t("findus.hours")}
                value={t("findus.hours_value")}
                testid="info-hours"
              />

              {/* Social media links */}
              <div className="pt-6 mt-2 border-t border-[#D8D0C5]/60">
                <p
                  className="text-xs uppercase tracking-[0.2em] text-[#8C847F] mb-4"
                  style={{ fontFamily: "'Lato', sans-serif" }}
                >
                  {t("findus.follow_us")}
                </p>
                <div className="flex items-center gap-3">
                  <a
                    href="https://www.facebook.com/share/1CXw9n31Dz/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 rounded-full border border-[#D8D0C5] flex items-center justify-center
                               text-[#5D544F] hover:text-white hover:bg-[#C1624D] hover:border-[#C1624D]
                               transition-all duration-300"
                    data-testid="contact-social-facebook"
                    aria-label="Facebook"
                  >
                    <Facebook size={18} />
                  </a>
                  <a
                    href="https://www.instagram.com/ancienne_poste_entrechaux/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 rounded-full border border-[#D8D0C5] flex items-center justify-center
                               text-[#5D544F] hover:text-white hover:bg-[#C1624D] hover:border-[#C1624D]
                               transition-all duration-300"
                    data-testid="contact-social-instagram"
                    aria-label="Instagram"
                  >
                    <Instagram size={18} />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="bg-white/80 backdrop-blur-sm p-8 md:p-10 border border-[#D8D0C5] rounded-sm shadow-sm">
              <h3
                className="text-2xl md:text-3xl font-light text-[#2C2420] mb-2"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {t("findus.write_us")}
              </h3>
              <p
                className="text-sm text-[#8C847F] mb-8"
                style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
              >
                {t("findus.write_us_subtitle")}
              </p>

              {sent ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-12 text-center"
                  data-testid="contact-success"
                >
                  <div className="w-16 h-16 rounded-full bg-[#6F7D4E]/10 flex items-center justify-center mx-auto mb-4">
                    <Send size={24} className="text-[#6F7D4E]" />
                  </div>
                  <p className="text-xl text-[#2C2420] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {t("findus.form_success_title")}
                  </p>
                  <p className="text-sm text-[#8C847F]" style={{ fontFamily: "'Lato', sans-serif" }}>
                    {t("findus.form_success_desc")}
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    className="mt-6 text-sm text-[#C1624D] underline underline-offset-4 hover:opacity-70 transition-opacity"
                    data-testid="send-another-btn"
                  >
                    {t("findus.send_another")}
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] text-[#8C847F] mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
                      {t("findus.form_name")}
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#D8D0C5] rounded-sm text-[#2C2420]
                                 focus:outline-none focus:border-[#C1624D] transition-colors duration-300"
                      style={{ fontFamily: "'Lato', sans-serif" }}
                      placeholder={t("findus.form_name_placeholder")}
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] text-[#8C847F] mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
                      {t("findus.form_email")}
                    </label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#D8D0C5] rounded-sm text-[#2C2420]
                                 focus:outline-none focus:border-[#C1624D] transition-colors duration-300"
                      style={{ fontFamily: "'Lato', sans-serif" }}
                      placeholder={t("findus.form_email_placeholder")}
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] text-[#8C847F] mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
                      {t("findus.form_message")}
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#D8D0C5] rounded-sm text-[#2C2420]
                                 focus:outline-none focus:border-[#C1624D] transition-colors duration-300 resize-none"
                      style={{ fontFamily: "'Lato', sans-serif" }}
                      placeholder={t("findus.form_message_placeholder")}
                      data-testid="contact-message-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full py-3 rounded-full bg-[#C1624D] text-white font-serif italic text-lg
                               hover:bg-[#A6503C] active:scale-[0.98] transition-all duration-300 shadow-md hover:shadow-lg h-auto"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    data-testid="contact-submit-btn"
                  >
                    {sending ? t("findus.form_sending") : t("findus.form_send")}
                  </Button>
                </form>
              )}
            </div>
          </motion.div>
        </div>

        {/* Recruitment Section */}
        <RecruitmentBlock isInView={isInView} />
      </div>
    </section>
  );
};
